import axios from "axios";
import configureStore from '../store/store';
import config from "../helper/Config";
import { saveState } from "../store/persisted.store";
import swal from 'sweetalert';
import { saveAs } from 'file-saver';
import ls from 'localstorage-slim';

var store = configureStore()

let axionConfig = {
	headers: {
		'Authorization': 'Bearer ' + store.getState().user.token.accessToken,
		'Content-Type': 'application/json'
	}
};

let API_URL = config.API_URL;
let API_URL_OLD = config.API_URL_OLD;
let WEB_URL = config.WEB_URL;

// MB-111. Every caller reaches this as `onError(error, <the function that failed>, ...the
// arguments it was called with)`, and on a 401 the request is retried with exactly those
// arguments once the token has been refreshed.
//
// It used to take the retry arguments as two fixed slots, `data` and `table`, and replay
// `func(data, table)`. Most callers pass two, so it looked right -- but `procedure(method,
// query, body)` and `executeJSON(method, query, body)` pass THREE. `procedure` lost the SP
// NAME and the payload (`onError(error, procedure, method, body)`), so a Save re-sent after
// a token refresh went to a garbage URL with no body: nothing was written, and because the
// retry "succeeded" nothing was reported either. In `executeJSON` the third argument landed
// on `hideAlert` instead, so a truthy body SILENCED the error dialog.
//
// The retry arguments are variadic now. `hideAlert` is still accepted, as the LAST argument
// and only when it is a boolean -- no caller passes a boolean as a retry argument, and the
// alternative was editing seventy call sites.
const onError = async (err, func, ...args) => {
	const lastIsFlag = args.length > 0 && typeof args[args.length - 1] === "boolean";
	const hideAlert = lastIsFlag ? args[args.length - 1] : false;
	const retryArgs = lastIsFlag ? args.slice(0, -1) : args;
	console.log(err);
	if (!err.response) {
		if (hideAlert) return
		swal({
			title: "Sorry,",
			icon: "error",
			text: "We couldn't provide this page.\nPlease try again later",
			buttons: {
				confirm: {
					text: 'OK',
					value: true,
					visible: true,
					className: "bg-danger",
					closeModal: true
				}
			}
		}
		);
	}
	else if (err?.response?.status == 401) {
		return await refreshToken(func, retryArgs)
	}
	else {
		if (hideAlert) return
		swal({
			title: "Sorry,",
			icon: "error",
			text: "We couldn't provide this page.\nPlease try again later",
			buttons: {
				confirm: {
					text: 'OK',
					value: true,
					visible: true,
					className: "bg-danger",
					closeModal: true
				}
			}
		}
		)
	}
}

const refreshToken = async (func, retryArgs = []) => {
	let opt = {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		url: `${API_URL}/api/Authentication/RefreshToken`,
		data: {
			"accessToken": store.getState().user.token.accessToken,
			"refreshToken": store.getState().user.token.refreshToken
		}
	};

	// MB-111: this call used to sit OUTSIDE the try. When the session was genuinely dead the
	// refresh itself returned 401, the rejection escaped past the catch below, and the user
	// got no redirect to the login page and no "your session ended" message at all -- Save
	// simply did nothing and the screen sat there looking slow. Inside the try, a dead
	// session takes the same path as a malformed token response: clear it and go to login.
	try {
		let res = await axios(opt);
		let newToken = res.data.data.token;
		store.getState().user.token.accessToken = newToken.accessToken;
		if (newToken.refreshToken) store.getState().user.token.refreshToken = newToken.refreshToken;
		if (newToken.expiredOn) store.getState().user.token.expiredOn = newToken.expiredOn;
		saveState(store.getState());
		axionConfig.headers.Authorization = 'Bearer ' + newToken.accessToken;

		// Sync the updated token to localStorage so other apps (e.g. equip-inventory) get the fresh token
		const userToken = JSON.stringify(store.getState().user.token);
		ls.set(`/${config.BASE_PATH.toLowerCase()}_token`, userToken, { encrypt: false });

		return await func(...retryArgs)
	} catch (error) {
		localStorage.removeItem(`/${config.BASE_PATH}`)
		localStorage.removeItem(`/${config.BASE_PATH.toLowerCase()}_token`)
		window.location.pathname = `${config.BASE_URL}/login`;
	}

}

export const getDataTable = async (data, table) => {
	axionConfig.method = 'POST';
	axionConfig.data = data;
	axionConfig.url = `${API_URL}/api/DataRepository/fetch/${encodeURIComponent(table)}`;

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, getDataTable, data, table)
	}
};


// ---- Bulk Issue Voucher (Setup > Membership > Bulk Issue Voucher) ----

// Multipart upload of the Excel + selected voucher, straight to the Core API.
export const submitBulkIssueVoucher = async (file, voucherMasterID) => {
	const formData = new FormData();
	formData.append("file", file);
	formData.append("voucherMasterID", voucherMasterID);
	const headers = { 'Authorization': 'Bearer ' + store.getState().user.token.accessToken };
	const response = await fetch(`${API_URL}/api/BulkIssueVoucher/Submit`, {
		method: 'POST',
		body: formData,
		headers
	});
	return await response.json();
};

// One page of the import history for a voucher. The API filters + pages server-side
// and returns TotalCount on each row, so the caller never has to fetch everything.
export const getBulkIssueVoucherBatches = async (voucherMasterID, skip = 0, take = 10) => {
	axionConfig.method = 'GET';
	axionConfig.data = null;
	axionConfig.url = `${API_URL}/api/BulkIssueVoucher/List`
		+ `?voucherMasterID=${encodeURIComponent(voucherMasterID || '')}`
		+ `&skip=${skip}&take=${take}`;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		// Retry through a closure: refreshToken re-invokes func(data, table), so a bare
		// function reference would re-fetch with the arguments lost (page 1, no filter).
		return onError(error, () => getBulkIssueVoucherBatches(voucherMasterID, skip, take));
	}
};

export const getBulkIssueVoucherItems = async (batchId) => {
	axionConfig.method = 'GET';
	axionConfig.data = null;
	axionConfig.url = `${API_URL}/api/BulkIssueVoucher/Items/${encodeURIComponent(batchId)}`;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, () => getBulkIssueVoucherItems(batchId));
	}
};

export const getDataTableImporter = async (data, table) => {
	axionConfig.method = 'POST';
	axionConfig.data = data;
	axionConfig.url = `${API_URL}/api/DataRepository/fetch/${encodeURIComponent(table)}`;
	let response = await axios(axionConfig);
	return response.data;
}

export const dataTableVirtualStock = async (data, method) => {
	axionConfig.method = 'POST';
	axionConfig.data = data;
	axionConfig.url = `${API_URL}/api/OutletVirtualStockBalance/${encodeURIComponent(method)}`;

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, dataTableVirtualStock, data, method)
	}
};

export const getDataTableSetting = async (group) => {
	axionConfig.method = 'GET';
	axionConfig.url = `${API_URL}/API/AppSetting/FetchByGroupId/${encodeURIComponent(group)}`;

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, getDataTableSetting, group)
	}
};

export const getDataTableSettingByKey = async (key) => {
	axionConfig.method = 'GET';
	axionConfig.url = `${API_URL}/API/AppSetting/FetchByKey/${encodeURIComponent(key)}`;

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, getDataTableSettingByKey, key)
	}
};

export const updateDataTableSetting = async (data) => {
	axionConfig.method = 'POST';
	axionConfig.data = data
	axionConfig.url = `${API_URL}/API/AppSetting/UpdateSetting`;

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, updateDataTableSetting, data)
	}
};

export const updateDataTableAttribute = async (data) => {
	axionConfig.method = 'POST';
	axionConfig.data = data
	axionConfig.url = `${API_URL}/api/AppSetting/UpdateAttributes`;

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, updateDataTableAttribute, data)
	}
};

export const updateDataTable = async (data, table) => {
	axionConfig.method = 'POST'
	axionConfig.data = data
	axionConfig.url = `${API_URL}/api/DataRepository/Update/${encodeURIComponent(table)}`

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, updateDataTable, data, table)
	}
};

export const insertDataTable = async (data, table) => {
	axionConfig.method = 'POST'
	axionConfig.data = data
	axionConfig.url = `${API_URL}/api/DataRepository/Insert/${encodeURIComponent(table)}`

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, insertDataTable, data, table)
	}
};

export const insertDataImporter = async (data) => {
	axionConfig.method = 'POST'
	axionConfig.data = data
	axionConfig.url = `${API_URL_OLD}/DataImporter/SubmitDataImport`

	try {
		let response = await axios(axionConfig);

		return response;
	} catch (error) {
		console.log(error)
		return onError(error, insertDataImporter, data)
	}
};

export const exportDataImporter = async (data, action) => {
	let axionConfig2 = JSON.parse(JSON.stringify(axionConfig));
	axionConfig2.method = 'POST'
	axionConfig2.data = data
	axionConfig2.url = `${API_URL_OLD}/DataImporter/${encodeURIComponent(action)}`
	axionConfig2.responseType = 'blob'

	try {
		let response = await axios(axionConfig2);

		const dirtyFileName = response.headers['content-disposition'];
		const regex = /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/;
		var fileName = dirtyFileName.match(regex)[3];

		var blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
		saveAs(blob, fileName ? fileName : `Export_Result_${new Date().getTime()}.xlsx`);

		return response;
	} catch (error) {
		console.log(error)
		return { message: "Failed to export data" }
	}
};

// Product Importer item export only. Same download as exportDataImporter, but on
// failure returns the API's own message (the error body arrives as a Blob because
// responseType is 'blob'). Kept separate so the other importer pages are unchanged.
export const exportItemImporter = async (data) => {
	let axionConfig2 = JSON.parse(JSON.stringify(axionConfig));
	axionConfig2.method = 'POST'
	axionConfig2.data = data
	axionConfig2.url = `${API_URL_OLD}/DataImporter/ExportItem`
	axionConfig2.responseType = 'blob'

	try {
		let response = await axios(axionConfig2);

		const dirtyFileName = response.headers['content-disposition'] || '';
		const regex = /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/;
		const match = dirtyFileName.match(regex);
		const fileName = match && (match[3] || match[2]);

		var blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
		saveAs(blob, fileName ? fileName : `Export_Result_${new Date().getTime()}.xlsx`);

		return response;
	} catch (error) {
		console.log(error)
		let message = null;
		const body = error?.response?.data;
		try {
			const text = body instanceof Blob ? await body.text() : (typeof body === 'string' ? body : null);
			const json = text ? JSON.parse(text) : body;
			message = json?.message || json?.Message || null;
		} catch (e) { }
		return { message: message || "Failed to export data" }
	}
};

export const fileManager = async (fileManagerUrl, file, addPath, useAuth,hideAlert) => {
	const formData = new FormData()
	formData.append("file", file)
	let headers = {};
	if(useAuth) headers.Authorization = 'Bearer ' + store.getState().user.token.accessToken;
	return await fetch(`${fileManagerUrl}${useAuth?"/pvt":""}/upload/${config.BASE_PATH}${addPath ? '/' + addPath : ''}`, {
		method: 'POST',
		body: formData,
		headers: headers
	})
		.then(response => response.json())
		.then(response => {
			return response
		})
		.catch((error) => {
			console.error('Error:', error);
			return onError(error, fileManager, file, !!hideAlert)
		});
};

export const getPvtFileManager = async (fileManagerUrl, body) => {
	axionConfig.method = 'POST'
	axionConfig.data = body
	axionConfig.url = `${fileManagerUrl}/pvt/sign`

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, fileManagerUrl, body)
	}
};


export const fileManagerCompany = async (fileManagerUrl, file) => {
	const formData = new FormData()
	formData.append("file", file)

	return await fetch(`${fileManagerUrl}/upload/${config.BASE_PATH}`, {
		method: 'POST',
		body: formData,
	})
		.then(response => response.json())
		.then(response => {
			return response
		})
		.catch((error) => {
			console.error('Error:', error);
			return onError(error, fileManagerCompany, file)
		});
};



export const itemQuantityTrigger = async (data, action) => {
	axionConfig.method = 'POST'
	axionConfig.data = data
	axionConfig.url = `${API_URL}/api/itemquantitytrigger/${encodeURIComponent(action)}`

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, itemQuantityTrigger, data, action)
	}
};

export const deliveryOrder = async (method,data, action) => {
	axionConfig.method = method;
	axionConfig.data = data;
	if (action) {
		axionConfig.url = `${API_URL_OLD}/deliveryorder/${encodeURIComponent(action)}`
	} else {
		axionConfig.url = `${API_URL_OLD}/deliveryorder`
	}
	

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, deliveryOrder, data, action)
	}

}


export const printRequest = async (data, param) => {
	axionConfig.method = 'POST';
	axionConfig.data = data;
	axionConfig.url = `${WEB_URL}/print/request.aspx${(param||"")!=""?`?${param}`:""}`

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, printRequest, data);
	}

}

export const printDirect = async (data) => {
    axionConfig.method = 'POST';
    axionConfig.data = data;
    axionConfig.url = `${WEB_URL}/print/directprint.aspx`;
    axionConfig.responseType = 'blob'; // Set response type to blob for PDF

    try {
        let response = await axios(axionConfig);
        
        // Create blob from response data
        const blob = new Blob([response.data], { type: 'application/pdf' });
        
        // Create temporary URL for the blob
        const url = window.URL.createObjectURL(blob);
        
        // Create temporary link element
        const link = document.createElement('a');
        link.href = url;
        
        // Get filename from header or use default
        const fileName = response.headers['content-disposition']
            ? response.headers['content-disposition'].split('filename=')[1]
            : `print_${new Date().getTime()}.pdf`;
            
        link.setAttribute('download', fileName);
        
        // Append to document, click and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up URL
        window.URL.revokeObjectURL(url);
        
        return { success: true };
    } catch (error) {
        return onError(error, printDirect, data);
    }

}

export const upsertDataTable = async (data, table) => {
	axionConfig.method = 'POST'
	axionConfig.data = data
	axionConfig.url = `${API_URL}/api/DataRepository/upsert/${encodeURIComponent(table)}`

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, upsertDataTable, data, table)
	}
};

export const bulkUpsertDataTable = async (data, table) => {
	axionConfig.method = 'POST'
	axionConfig.data = data
	axionConfig.url = `${API_URL}/api/DataRepository/bulkupsert/${encodeURIComponent(table)}`

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, upsertDataTable, data, table)
	}
};

export const apiProduct = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/Product/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiProduct, method, action, body);
	}
};

export const apiBundle = async (method, action, body) => {

	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/bundle/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiBundle, method, action, body);
	}
};

export const apiAttribute = async (method, action, body) => {

	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/attributes/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiAttribute, method, action, body);
	}
};

export const apiPromotion = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/promotion/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiPromotion, method, action, body);
	}
};

export const apiUser = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/user/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiUser, method, action, body);
	}
};

export const apiRolePrivilege = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/RolePrivileges/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiRolePrivilege, method, action, body);
	}
};

export const apiMembership = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/membership/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiMembership, method, action, body);
	}
};

export const apiActivePayment = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/ActivePayment/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiActivePayment, method, action, body);
	}
};

export const apiHotkey = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/Hotkey/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiHotkey, method, action, body);
	}
};

export const apiQtyTrigger = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/ItemQuantityTrigger/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiQtyTrigger, method, action, body);
	}
};

export const apiOutletOrderingSetting = async(method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/outletorderingsetting/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiQtyTrigger, method, action, body);
	}
}

export const apiPaymentGateway = async(method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/paymentgateway/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiQtyTrigger, method, action, body);
	}
}

export const getAppMenu = async (param) => {
	axionConfig.method = 'GET'
	axionConfig.url = `${API_URL}/api/AppModule/GetMenu/${encodeURIComponent(param)}`

	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, getAppMenu, param)
	}
};

export const getItemDepartmentDropDown = async (o, t) => {
	let axionConfig = {
		method: 'post',
		url: API_URL + '/api/DataRepository/fetch/itemdepartment',
		headers: {
			'Authorization': 'Bearer ' + t,
			'Content-Type': 'application/json'
		},
		data: o
	};

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, getItemDepartmentDropDown, o, t)
	}
};

export const doLogin = async (o) => {
	let opt = {
		headers: {
			'Content-Type': 'application/json'
		}
	};

	opt.method = 'POST'
	opt.data = o;
	opt.url = `${API_URL}/api/Authentication/Login`;
	let res = await axios(opt);
	return res.data;
};


export const doValidateOTP = async (o) => {
	let opt = {
		headers: {
			'Content-Type': 'application/json'
		}
	};

	opt.method = 'POST'
	opt.data = o;
	opt.url = `${API_URL}/api/Authentication/ValidateOtp`;
	let res = await axios(opt);
	return res.data;
};



export const doValidateResetOTP = async (o) => {
	let opt = {
		headers: {
			'Content-Type': 'application/json'
		}
	};

	opt.method = 'POST'
	opt.data = o;
	opt.url = `${API_URL}/api/Authentication/ValidateResetPasswordOtp`;
	let res = await axios(opt);
	return res.data;
};


export const doRequestForgotPassword = async (o) => {
	let opt = {
		headers: {
			'Content-Type': 'application/json'
		}
	};

	opt.method = 'POST'
	opt.data = o;
	opt.url = `${API_URL}/api/Authentication/RequestResetPassword`;
	let res = await axios(opt);
	return res.data;
};

export const getHeaderColumn = async (table) => {

	axionConfig.method = 'GET';
	axionConfig.url = `${API_URL}/api/DataRepository/fields/${encodeURIComponent(table)}`;
	axionConfig.data = null;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, getHeaderColumn, table);
	}
}

export const apiCollection = async (method, action, body) => {

	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/Collection/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiProduct, method, action, body);
	}
}

export const apiStockTemplate = async (method, action, body) => {

	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/StockTemplate/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiStockTemplate, method, action, body);
	}
}

export const apiProductMinMax = async (method, action, body) => {

	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/productMinMax/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiProductMinMax, method, action, body);
	}
}

export const apiCommission = async (method, action, body) => {

	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/commission/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiCommission, method, action, body);
	}
}

export const apiPointOfSale = async (method, action, body) => {

	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/pointofsale/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiPointOfSale, method, action, body);
	}
}

export const bulkUpdate = async (data, table) => {
	axionConfig.method = 'POST'
	axionConfig.data = data
	axionConfig.url = `${API_URL}/api/DataRepository/bulkupdate/${encodeURIComponent(table)}`

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, bulkUpdate, data, table)
	}
}

export const simulateSales = async (data) => {
	let axiosConfig2 = JSON.parse(JSON.stringify(axionConfig));
	axiosConfig2.method = 'POST'
	axiosConfig2.data = data
	axiosConfig2.url = `${API_URL_OLD}/SimulateSales/AddItem`;

	try {
		let response = await axios(axiosConfig2);
		return response.data;
	} catch (error) {
		return onError(error, simulateSales, data)
	}
}

export const getVersion = async () => {
	let axiosConfig2 = JSON.parse(JSON.stringify(axionConfig));
	axiosConfig2.method = 'GET'
	axiosConfig2.url = `${API_URL}/api/Info/Version`;

	try {
		let response = await axios(axiosConfig2);
		return response.data;
	} catch (error) {
		return onError(error, getVersion)
	}
}

export const getSupplierMapping = async (id) => {
	axionConfig.method = 'GET';
	axionConfig.url = `${API_URL}/api/supplier/FetchSupplierInventoryMapping/${encodeURIComponent(id)}`;
	axionConfig.data = null;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, getHeaderColumn, id);
	}
}

export const apiSupplier = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/supplier/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiCommission, method, action, body);
	}
}


export const apiCustomField = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/CustomField/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiDiscount, method, action, body);
	}
};


export const apiWarehouseMapping = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/WarehouseProduct/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiCustomField, method, action, body);
	}
};

export const apiDiscount = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/Discount/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiDiscount, method, action, body);
	}
};

export const apiAppCommand = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/AppCommand/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiAppCommand, method, action, body);
	}
};

export const apiResetPassword = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/ResetPassword/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiResetPassword, method, action, body);
	}
};

export const getParameterExportTool = async (procedureName) => {
	axionConfig.method = 'GET';
	axionConfig.url = `${API_URL}/api/AppModule/GetExportParameters/${procedureName}`;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, getParameterExportTool, procedureName);
	}
}

export const getSourceDropDownExportTool = async (dropDownId) => {
	axionConfig.method = 'GET';
	axionConfig.url = `${API_URL}/api/AppModule/GetDropDown/${dropDownId}`;
	try {
		let response = await axios(axionConfig);
		return await response.data;
	} catch (error) {
		return onError(error, getSourceDropDownExportTool, dropDownId);
	}
}

export const getDropdownOption = async (ID, showALL=false) => {
	try {
		let val = await getSourceDropDownExportTool(ID);
		let el = [];
		
		if(showALL)
			el = [{
                "label": 'ALL',
                "value": 'ALL'
            }]

		if (val) {
			for (const ou of val) {
				el.push({
					"label": ou?.text,
					"value": ou?.value
				})
			}
		} else {
			swal({
				text: "Failed to fetch outlet dropdown",
				icon: "error"
			});
		}
		return el;
	} catch (error) {
		console.log(error);
		return []
	}
}

export const SubmitDataExport = async (body) => {
	let method = 'POST';
	let action = 'submitdataexport';
	axionConfig.method = method;
	axionConfig.url = `${API_URL_OLD}/dataexporter/${action}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, SubmitDataExport, method, action, body);
	}
}


export const apiListValueCategory = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/ListValueCategory/${action}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiDiscount, method, action, body);
	}
}

export const getSupplierMapAvailableDropdown = async (itemno) => {
	axionConfig.method = 'GET';
	axionConfig.url = `${API_URL}/API/Product/FetchSupplierMapDropown/${itemno}`;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, getDataTableSetting, itemno)
	}
};

export const apiPaymentProvider = async (method, path, body, provider_url) => {
	axionConfig.method = method;
	axionConfig.url = `${provider_url}/api/${path}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiDiscount, method, path, body);
	}
}

export const apiPOSTerminalSetting = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/posterminalsettings/${action}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiDiscount, method, action, body);
	}
};

export const executeSP = async (spName, body, method="POST") => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/DataRepository/Execute/${spName}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, apiDiscount, spName, body);
	}
};

export const execute = async (method,spName, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/DataRepository/Execute/${spName}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, apiDiscount, spName, body);
	}
};

export const executeJSON = async (method,query, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/DataRepository/executejson/${query}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, executeJSON, method, query, body);
	}
};


export const apiOutlet = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/Outlet/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiOutlet, method, action, body);
	}
};


export const apiMembershipIntegrationSetting = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/membershipintegration/${action}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiDiscount, method, action, body);
	}
};

export const apiServiceGeneral = async (method, url, endpoint, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${url}/${endpoint}/${action}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiDiscount, method, action, body);
	}
};

export const apiAuthentication = async (method, action, body, token) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/authentication/${action}`;

	if (token) {
		axionConfig.headers = {
			'Authorization': `Bearer ${token}`,
			'Content-Type': 'application/json'
		}
	}

	axionConfig.data = body;

	if (token) {
		axionConfig.headers = {
			'Authorization': `Bearer ${token}`,
			'Content-Type': 'application/json'
		}
	}


	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiAuthentication, method, action, body);
	}
};

export const apiLogin = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/login/${action}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiLogin, method, action, body);
	}
};


export const procedure = async (method, query, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/DataRepository/procedure/${query}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, procedure, method, query, body);
	}
};

export const apiPromo = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/promo/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiPromo, method, action, body);
	}
};

export const getSourceBlankOption = async (dropDownId) => {
	axionConfig.method = 'GET';
	axionConfig.url = `${API_URL}/api/AppModule/GetBlankTemplateOption/${dropDownId}`;
	try {
		let response = await axios(axionConfig);
		return await response.data;
	} catch (error) {
		return onError(error, getSourceBlankOption, dropDownId);
	}
}


export const downloadTemplateImporter = async (dropDownId,mode) => {
	
	let axionConfig2 = JSON.parse(JSON.stringify(axionConfig));
	axionConfig2.method = 'GET'
	// axionConfig2.data = data
	axionConfig2.url = `${API_URL_OLD}/DataExporter/ExportBlankTemplate/${dropDownId}/${mode}`
	axionConfig2.responseType = 'blob'

	try {
		let response = await axios(axionConfig2);

		let blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
		saveAs(blob, `Template_Export_Result_${new Date().getTime()}.xlsx`);

		return response;
	} catch (error) {
		console.log(error)
		return { message: "Failed to export data" }
	}
}

export const getStatusExporter = async (appDataExporterId) => {
	axionConfig.method = 'GET'
	axionConfig.url = `${API_URL_OLD}/DataExporter/status/${appDataExporterId}`

	try {
		let response = await axios(axionConfig);

		return response;
	} catch (error) {
		console.log(error)
		return onError(error, getStatusExporter, appDataExporterId)
	}
};

export const reward = async (method, path, body) => {
	axionConfig.method = method
	axionConfig.url = `${API_URL_OLD}/Reward/${path}`
	axionConfig.data = body;

	try {
		let response = await axios(axionConfig);

		return response;
	} catch (error) {
		return error.response
	}
};


export const procedureV2 = async (method,spName, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/DataRepository/procedure/${spName}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, procedureV2, method, spName, body);
	}
};


export const publicProcedure = async (method,spName, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/PublicDataRepository/procedure/${spName}`;

	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, publicProcedure, method, spName, body);
	}
};

export const getSamlConfig = async() => {
	axionConfig.method = 'GET';
	axionConfig.url = `${API_URL}/api/PublicDataRepository/procedure/getsaml`;

	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		let response = {
			data : []
		}
		return response;
	}
}

export const apiOnlineMenu = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/onlinemenu/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response;
	} catch (error) {
		return onError(error, apiOnlineMenu, method, action, body);
	}
};


export const apiMessagingBlacklist = async (method, action, body) => {
	axionConfig.method = method;
	axionConfig.url = `${API_URL}/api/MessagingBlacklist/${action}`;
	axionConfig.data = body;
	try {
		let response = await axios(axionConfig);
		return response.data;
	} catch (error) {
		return onError(error, apiMessagingBlacklist, method, action, body);
	}
};

export default doLogin;
